import { format, isValid, parse } from 'date-fns';
import type {
  RaffleExclusion,
  StaffTicketCountsResponse,
  StaffTicketMonth,
  StaffTicketPerson,
  StaffTicketRole,
  StaffTicketStanding,
} from '../types/staff';

/**
 * Helpers for the steward ticket counts (`/staff/ticket-counts`): six months of tickets closed
 * per staff member, each finalized month's MVP (most tickets) and the raffle drawn from the
 * runners-up. The MVP sits the raffle out; everyone else's tickets earn entries.
 */

const MONTH_RE = /^\d{4}-\d{2}$/;

/** `2026-04` → `Apr 2026` (`long`: `April 2026`, `name`: `Apr`). Anything unparseable is returned as-is. */
export function formatTicketMonth(month: string, style: 'short' | 'long' | 'name' = 'short'): string {
  if (!MONTH_RE.test(month)) return month;
  const date = parse(month, 'yyyy-MM', new Date(2000, 0, 1));
  if (!isValid(date)) return month;
  return format(date, style === 'long' ? 'MMMM yyyy' : style === 'name' ? 'MMM' : 'MMM yyyy');
}

export const findTicketPerson = (data: StaffTicketCountsResponse, discordId: string): StaffTicketPerson | null =>
  data.staff.find((s) => s.discord_id === discordId) ?? null;

/** The open month (last in the list) — the one `/ticketcount` reports live. */
export const currentTicketMonth = (data: StaffTicketCountsResponse): StaffTicketMonth | null =>
  data.months.length > 0 ? data.months[data.months.length - 1] : null;

/** Newest month whose raffle has run. */
export function latestFinalizedMonth(data: StaffTicketCountsResponse): StaffTicketMonth | null {
  for (let i = data.months.length - 1; i >= 0; i--) {
    if (data.months[i].finalized) return data.months[i];
  }
  return null;
}

/** A person's standing for one month; null when the month is absent from their record. */
export const monthStanding = (person: StaffTicketPerson, month: string): StaffTicketStanding | null =>
  person.months?.[month] ?? null;

export interface TicketRankRow {
  person: StaffTicketPerson;
  standing: StaffTicketStanding;
  /** 1-based, ties share a rank (1, 1, 3). `null` for the bot, which closes tickets but is never ranked. */
  rank: number | null;
}

/**
 * Everyone with a known count for `month`, most tickets first (ties by name). Unknown months are
 * left out rather than shown as zero, matching the API's own `total_known_tickets` rule.
 */
export function rankTicketMonth(data: StaffTicketCountsResponse, month: string): TicketRankRow[] {
  const known = data.staff
    .map((person) => ({ person, standing: monthStanding(person, month) }))
    .filter((row): row is { person: StaffTicketPerson; standing: StaffTicketStanding } => !!row.standing && row.standing.known)
    .sort((a, b) => b.standing.tickets - a.standing.tickets || a.person.name.localeCompare(b.person.name));

  let position = 0;
  let rank = 0;
  let previous = -1;
  return known.map((row) => {
    if (row.person.role === 'bot') return { ...row, rank: null };
    position += 1;
    if (row.standing.tickets !== previous) {
      rank = position;
      previous = row.standing.tickets;
    }
    return { ...row, rank };
  });
}

/** One person's rank among the ranked (non-bot) staff for a month; null when they have no known count. */
export function personTicketRank(data: StaffTicketCountsResponse, discordId: string, month: string): { rank: number; of: number } | null {
  const ranked = rankTicketMonth(data, month).filter((r) => r.rank !== null);
  const mine = ranked.find((r) => r.person.discord_id === discordId);
  return mine && mine.rank !== null ? { rank: mine.rank, of: ranked.length } : null;
}

/** Tickets closed by ranked staff (bot excluded) in a month, known counts only. */
export function monthTicketTotal(data: StaffTicketCountsResponse, month: string): number {
  return rankTicketMonth(data, month).reduce((n, r) => (r.rank === null ? n : n + r.standing.tickets), 0);
}

export type StaffHonourKind = 'mvp' | 'raffle';

export interface StaffHonour {
  month: string;
  kind: StaffHonourKind;
  tickets: number;
  entries: number | null;
}

/** MVP months and raffle wins for one person, newest first (MVP before raffle within a month). */
export function personHonours(person: StaffTicketPerson): StaffHonour[] {
  const out: StaffHonour[] = [];
  for (const [month, s] of Object.entries(person.months ?? {})) {
    if (s.is_mvp) out.push({ month, kind: 'mvp', tickets: s.tickets, entries: s.entries });
    if (s.is_raffle_winner) out.push({ month, kind: 'raffle', tickets: s.tickets, entries: s.entries });
  }
  return out.sort((a, b) => b.month.localeCompare(a.month) || a.kind.localeCompare(b.kind));
}

export interface MonthHonours {
  month: StaffTicketMonth;
  mvp: StaffTicketPerson | null;
  raffleWinner: StaffTicketPerson | null;
}

/** Finalized months, newest first, with the MVP and raffle winner resolved to people. */
export function monthHonours(data: StaffTicketCountsResponse): MonthHonours[] {
  return [...data.months]
    .filter((m) => m.finalized)
    .reverse()
    .map((month) => ({
      month,
      mvp: month.mvp_discord_id ? findTicketPerson(data, month.mvp_discord_id) : null,
      raffleWinner: month.raffle_winner_id ? findTicketPerson(data, month.raffle_winner_id) : null,
    }));
}

const EXCLUSIONS: Record<RaffleExclusion, string> = {
  mvp: 'MVP — sits out the raffle',
  below_minimum: 'Below the entry minimum',
  opted_out: 'Opted out',
  not_in_guild: 'Not in the guild',
  trainee: 'Trainee',
  not_eligible_role: 'Role not eligible',
  no_entries: 'No entries',
};

/** Human label for a raffle exclusion reason; unknown reasons are de-snaked. */
export function describeExclusion(reason: RaffleExclusion | string | null): string {
  if (!reason) return '';
  return (EXCLUSIONS as Record<string, string>)[reason] ?? reason.replace(/_/g, ' ');
}

export const entriesLabel = (n: number): string => `${n} ${n === 1 ? 'entry' : 'entries'}`;

/** One-line raffle standing for a person-month: `3 entries`, `Won the raffle · 6 entries`, `MVP — sits out the raffle`, … */
export function describeRaffleStanding(standing: StaffTicketStanding): string {
  if (!standing.known) return 'No record for this month';
  if (!standing.finalized) return 'Raffle not drawn yet';
  if (standing.is_raffle_winner) return `Won the raffle · ${entriesLabel(standing.entries ?? 0)}`;
  if (standing.excluded) return describeExclusion(standing.excluded);
  if (standing.entries !== null && standing.entries > 0) return entriesLabel(standing.entries);
  return 'No entries';
}

const SHORT_EXCLUSIONS: Record<string, string> = {
  mvp: 'MVP',
  below_minimum: 'below min',
  opted_out: 'opted out',
  not_in_guild: 'not in guild',
  trainee: 'trainee',
  not_eligible_role: 'ineligible',
  no_entries: '0 entries',
};

/** Raffle standing for tight spaces (chart labels): `3 entries`, `won · 6`, `MVP`, `opted out`, `live`, `` when unknown. */
export function raffleStandingShort(standing: StaffTicketStanding): string {
  if (!standing.known) return '';
  if (!standing.finalized) return 'live';
  if (standing.is_raffle_winner) return `won · ${standing.entries ?? 0}`;
  if (standing.excluded) return SHORT_EXCLUSIONS[standing.excluded] ?? standing.excluded.replace(/_/g, ' ');
  if (standing.entries !== null && standing.entries > 0) return entriesLabel(standing.entries);
  return '0 entries';
}

export function ticketRoleLabel(role: StaffTicketRole | string | null): string {
  switch (role) {
    case 'administrator':
      return 'Admin';
    case 'moderator':
      return 'Moderator';
    case 'helper':
      return 'Helper';
    case 'bot':
      return 'Bot';
    default:
      return role ? role.replace(/_/g, ' ') : 'Former staff';
  }
}

export interface TicketSeriesPoint {
  month: StaffTicketMonth;
  standing: StaffTicketStanding | null;
}

/** One point per month in the response (oldest first) for a person; absent months have `standing: null`. */
export function ticketSeries(data: StaffTicketCountsResponse, person: StaffTicketPerson): TicketSeriesPoint[] {
  return data.months.map((month) => ({ month, standing: monthStanding(person, month.month) }));
}
